// CommandPalette — ⌘K 全域搜尋(C11)。兩層結果:
//  • 導覽目標:role 過濾後的 manifest 導覽項(label/meta/slug 子字串比對,即時)
//  • 實體:GET /v3/admin/search(restful 02 §3.3.2)— email / user id / device id /
//    org 名精準優先 dispatch,>=3 字元才發、250ms debounce
// 「最近開啟」為 per-user 執行期狀態(localStorage),不入 manifest(C11 裁決)。

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import { SEARCH_ENTITY_LABEL, SEARCH_ENTITY_ROUTES } from '../platform/adminPlatformModel';
import type { AdminNavGroup, AdminNavItem } from '../platform/adminPlatformModel';

const RECENTS_KEY = 'adm.palette.recents';
const RECENTS_MAX = 6;

interface EntityHit {
  type: string;
  id: string;
  label: string;
  sublabel?: string;
}

interface Row {
  key: string;
  kind: 'nav' | 'entity';
  title: string;
  meta: string;
  route: string;
}

function readRecents(): string[] {
  try {
    const raw = JSON.parse(localStorage.getItem(RECENTS_KEY) ?? '[]');
    return Array.isArray(raw) ? raw.filter((r) => typeof r === 'string') : [];
  } catch {
    return [];
  }
}

function pushRecent(route: string) {
  const next = [route, ...readRecents().filter((r) => r !== route)].slice(0, RECENTS_MAX);
  try {
    localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch {
    /* storage unavailable — recents are best-effort */
  }
}

function navRow(item: AdminNavItem): Row {
  return { key: `nav:${item.to}`, kind: 'nav', title: item.label, meta: item.meta, route: item.to };
}

function entityRow(hit: EntityHit): Row {
  const toRoute = SEARCH_ENTITY_ROUTES[hit.type];
  return {
    key: `ent:${hit.type}:${hit.id}`,
    kind: 'entity',
    title: hit.label,
    meta: `${SEARCH_ENTITY_LABEL[hit.type] ?? hit.type}${hit.sublabel ? ` · ${hit.sublabel}` : ''}`,
    route: toRoute ? toRoute(hit.id) : '/',
  };
}

export function CommandPalette({ groups }: { groups: AdminNavGroup[] }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [entities, setEntities] = useState<EntityHit[]>([]);
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const reqSeq = useRef(0);

  const navItems = useMemo(() => groups.flatMap((g) => g.items.filter((i) => !i.soon)), [groups]);

  const openRef = useRef(false);
  openRef.current = open;

  // global hotkey: ⌘K / Ctrl+K toggles; Esc closes ONLY the palette when it is
  // open (capture + stopPropagation so an underlying ConfirmDialog's own Esc
  // handler doesn't fire in the same keystroke).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape' && openRef.current) {
        e.stopPropagation();
        setOpen(false);
      }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, []);

  useEffect(() => {
    if (open) {
      setQuery('');
      setEntities([]);
      setActive(0);
      // focus after the overlay paints
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  // entity search: >=3 chars, debounced; stale responses dropped by seq.
  // The CLEAR paths must also bump the seq — otherwise an in-flight response
  // for a longer query lands after the user shortened/closed and resurrects
  // stale entity rows.
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const q = query.trim();
    if (!open || q.length < 3) {
      reqSeq.current++;
      setEntities([]);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      const seq = ++reqSeq.current;
      try {
        const res = await apiFetch<{ results?: EntityHit[] }>('/v3/admin/search', { query: { q } });
        if (seq === reqSeq.current) setEntities(Array.isArray(res.data?.results) ? res.data.results : []);
      } catch {
        if (seq === reqSeq.current) setEntities([]); // search is best-effort; nav rows still work
      }
    }, 250);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, open]);

  const rows: Row[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      const recents = readRecents()
        .map((route) => navItems.find((i) => i.to === route))
        .filter((i): i is AdminNavItem => Boolean(i))
        .map(navRow);
      return recents.length > 0 ? recents : navItems.map(navRow);
    }
    const nav = navItems
      .filter((i) => i.label.toLowerCase().includes(q) || i.meta.toLowerCase().includes(q) || i.to.toLowerCase().includes(q))
      .map(navRow);
    return [...nav, ...entities.map(entityRow)];
  }, [query, navItems, entities]);

  useEffect(() => {
    setActive((a) => Math.min(a, Math.max(0, rows.length - 1)));
  }, [rows.length]);

  const go = useCallback(
    (row: Row) => {
      setOpen(false);
      if (row.kind === 'nav') pushRecent(row.route);
      navigate(row.route);
    },
    [navigate]
  );

  function onInputKey(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rows.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter' && rows[active]) {
      e.preventDefault();
      go(rows[active]);
    }
  }

  if (!open) return null;

  return (
    <div className="palette-overlay" role="dialog" aria-modal="true" aria-label="全域搜尋" onClick={() => setOpen(false)}>
      <div className="palette" onClick={(e) => e.stopPropagation()}>
        <input
          ref={inputRef}
          className="palette-input"
          placeholder="搜尋頁面,或貼 email / 使用者 ID / 裝置 ID / 組織名…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onInputKey}
          aria-label="搜尋"
        />
        <div className="palette-list" role="listbox">
          {rows.length === 0 && <div className="palette-empty text-tertiary">沒有符合的頁面或實體。</div>}
          {rows.map((row, i) => (
            <button
              key={row.key}
              role="option"
              aria-selected={i === active}
              className={`palette-row${i === active ? ' is-active' : ''}`}
              onMouseEnter={() => setActive(i)}
              onClick={() => go(row)}
            >
              <span className={`palette-kind ${row.kind === 'entity' ? 'is-entity' : ''}`}>{row.kind === 'nav' ? '頁面' : '實體'}</span>
              <span className="palette-title">{row.title}</span>
              <span className="palette-meta text-tertiary">{row.meta}</span>
            </button>
          ))}
        </div>
        <div className="palette-foot text-tertiary">↑↓ 選擇 · Enter 開啟 · Esc 關閉</div>
      </div>
    </div>
  );
}
